import { faHistory, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AgGridAtom } from '../../../../components/atoms/atom';
import { getFormattedDate } from '../../../../utils/method';
import {
    InvoicePaymentAmountCell,
    SingleInvoiceAmountCell,
} from "./InvoiceListAmountCells";
import './PreviousInvoicesTable.scss';

const HistoricalDataCellRenderer = memo((props) => {
    const { t } = useTranslation();
    const isBaseline = props.data.isHistoricalBaseline;
    const historicalCount = props.data.historicalInvoices?.length || 0;
    const onHistoricalDataClick = props.onHistoricalDataClick;

    if (!isBaseline || historicalCount === 0) {
        return <div className="text-center text-secondary">-</div>;
    }

    const handleClick = (e) => {
        e.stopPropagation();
        if (onHistoricalDataClick) {
            onHistoricalDataClick(props.data);
        }
    };

    return (
        <div className="text-center mt-1">
            <span
                onClick={handleClick}
                className="pit-history-badge"
                title={t("invoice.click-to-view-historical-invoices")}
            >
                <FontAwesomeIcon icon={faHistory} />
                {historicalCount} {historicalCount === 1 ? t("invoice.invoice") : t("invoice.invoices")}
            </span>
        </div>
    );
});

const PreviousInvoicesTable = ({ invoices = [], onHistoricalDataClick }) => {
    const { t } = useTranslation();
    const currentInvoiceId = useSelector(state => state.invoiceV2?.formState?.id);

    // Filter out the current invoice if it exists in the invoices list
    const filteredInvoices = useMemo(() =>
        invoices.filter(invoice => invoice.id !== currentInvoiceId),
        [invoices, currentInvoiceId]
    );

    // Define AgGrid columns
    const columns = useMemo(() => [
        {
            field: "createdAt",
            headerName: t("invoice.provided"),
            valueFormatter: (params) => getFormattedDate(params.value),
            width: 130,
        },
        {
            field: 'invoiceNumber',
            headerName: t('invoice.invoice-number'),
            width: 150,
        },
        {
            field: "internalReferenceNumber",
            headerName: t("invoice.internal-reference-number"),
            width: 180,
        },
        {
            field: "historicalInvoices",
            headerName: t("invoice.historical-data"),
            width: 160,
            cellRenderer: HistoricalDataCellRenderer,
            cellRendererParams: {
                onHistoricalDataClick: onHistoricalDataClick
            }
        },
        {
            headerName: t("invoice.billing-period"),
            field: "dateInfo",
            width: 200,
            cellRenderer: (params) => {
                const { data } = params;
                const from = data.billingPeriodFrom ? getFormattedDate(data.billingPeriodFrom) : null;
                const to = data.billingPeriodTo ? getFormattedDate(data.billingPeriodTo) : null;
                const range = from && to ? `${from} – ${to}` : "–";
                const invoiceDate = data.invoiceDate ? getFormattedDate(data.invoiceDate) : "–";
                return (
                    <div className="pit-date-cell">
                        <span className="pit-date-cell__range">{range}</span>
                        <div className="pit-date-cell__meta">
                            <span className="pit-date-cell__meta-label">{t("invoice.invoice-date")}</span>
                            <span className="pit-date-cell__meta-value">{invoiceDate}</span>
                        </div>
                    </div>
                );
            },
        },
        {
            headerName: t("invoice.invoice-amount-net"),
            field: "totalAmount",
            type: "numericColumn",
            width: 150,
            cellRenderer: (params) => (
                <SingleInvoiceAmountCell value={params.data?.totalAmount} />
            ),
        },
        {
            headerName: t("invoice.verified-invoice-amount-net"),
            field: "aufmassTotalAmount",
            type: "numericColumn",
            width: 170,
            cellRenderer: (params) => (
                <SingleInvoiceAmountCell value={params.data?.aufmassTotalAmount} />
            ),
        },
        {
            headerName: t("invoice.payment-amount-net-gross"),
            field: "paymentAmountGross",
            type: "numericColumn",
            width: 180,
            cellRenderer: (params) => (
                <InvoicePaymentAmountCell data={params.data} t={t} />
            ),
        },
        {
            field: "status",
            headerName: t("invoice.status.status"),
            width: 160,
            cellRenderer: (params) => {
                const status = params.data.status;
                const statusKey = status?.toLowerCase().replace(/_/g, "-");
                return (
                    <span className={`pi-status-badge pi-status-badge--${statusKey}`}>
                        {t(`invoice.status.${status}`)}
                    </span>
                );
            },
        },
    ], [t, onHistoricalDataClick]);

    const rowStyle = useMemo(() => ({}), []);
    const defaultColDef = useMemo(() => ({
        sortable: false,
        wrapText: true,
        autoHeight: false,
        minWidth: 100
    }), []);

    const overlayNoRowsTemplate = useMemo(() =>
        `<span class="ag-overlay-no-rows-center">${t("invoice.no-previous-invoices")}</span>`,
        [t]
    );

    if (!filteredInvoices?.length) {
        return (
            <div className="alert alert-info d-flex align-items-center no-invoices-alert">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                {t("invoice.no-previous-invoices")}
            </div>
        );
    }

    return (
        <div className="previous-invoices-table">
            <div className="flex-grow-1 ag-grid-parent">
                <AgGridAtom
                    columnDefs={columns}
                    rowData={filteredInvoices}
                    domLayout="normal"
                    overlayNoRowsTemplate={overlayNoRowsTemplate}
                    rowStyle={rowStyle}
                    defaultColDef={defaultColDef}
                    rowHeight={60}
                    suppressRowTransform={true}
                />
            </div>
        </div>
    );
};

export default memo(PreviousInvoicesTable);
